import type { CompassKind, CompassSlot } from "odyssey-shared";
import { say } from "../../words.ts";
import { directionOf, distanceWords } from "../world-map/places.ts";

/**
 * The compass pips' words (P1.9-T190; ADR-061's amendment of 2026-10-14, item 11): the line that names a pip when it is
 * hovered, "quietmoth, 42 tiles north-east", and the visually hidden list that reads every pip in draw order, written
 * at most once a second.
 */

/**
 * The part of a pip its words are made from.
 */
export interface PipSubject {
  readonly kind: CompassKind;
  readonly name: string;
  readonly dx: number;
  readonly dy: number;
}

/**
 * The longest a hidden list may go between two writes, in milliseconds.
 */
export const COMPASS_LIST_INTERVAL = 1000;

/**
 * The line that names one pip: a party member or a place by name, distance and direction ("quietmoth, 42 tiles
 * north-east"), the pin by "Pin" and its distance and direction, a quest by "Quest" and its title. The distance is the
 * straight line to the target in whole tiles, as the Map's list gives it.
 *
 * @param pip What the pip stands for and where it lies from the player, in tiles.
 * @returns The words.
 */
export function pipWords(pip: PipSubject): string {
  if (pip.kind === "quest") {
    return say("minimap.pipQuest", { title: pip.name });
  }
  const distance = Math.round(Math.hypot(pip.dx, pip.dy));
  const direction = directionOf(pip.dx, pip.dy);
  if (pip.kind === "pin") {
    return say("worldMap.pinLine", { distance: say("worldMap.tiles", { count: distance }), direction: say(`worldMap.direction.${direction}`) });
  }
  return say("minimap.pipLine", { name: pip.name, where: distanceWords(distance, direction) });
}

/**
 * The hidden list's text: the frame's name, then every pip's line in draw order, top first ("Compass: Pin, 42 tiles
 * east; quietmoth, 12 tiles north").
 *
 * @param slots The pip buffer's slots.
 * @param count How many are in use.
 * @returns The text, or the empty text when there is no pip.
 */
export function compassListText(slots: readonly CompassSlot[], count: number): string {
  if (count === 0) {
    return "";
  }
  const lines: string[] = [];
  for (let at = 0; at < count; at += 1) {
    lines.push(pipWords(slots[at] as CompassSlot));
  }
  return say("minimap.compassList", { items: lines.join("; ") });
}

/**
 * The words a shown tooltip should now carry: the line of the slot it names, when that differs from what it shows.
 *
 * @param shown The words the tooltip shows.
 * @param slots The pip buffer's slots.
 * @param count How many are in use.
 * @param index The slot the tooltip names.
 * @returns The new words, or null when the slot is gone or the words are unchanged.
 */
export function refreshedTipText(shown: string, slots: readonly CompassSlot[], count: number, index: number): string | null {
  const slot = index >= 0 && index < count ? slots[index] : undefined;
  if (slot === undefined) {
    return null;
  }
  const text = pipWords(slot);
  return text === shown ? null : text;
}

/**
 * What the list's writer needs from its page.
 */
export interface CompassListPorts {
  readonly read: () => string;
  readonly write: (text: string) => void;
  readonly now: () => number;
  readonly later: (run: () => void, delay: number) => unknown;
  readonly cancel: (timer: unknown) => void;
}

/**
 * The hidden list's writer.
 */
export interface CompassList {
  readonly touch: () => void;
  readonly dispose: () => void;
}

/**
 * Makes the writer of the hidden list: `touch` is called whenever a pip may have changed, and the words are read and
 * written at most once a second (`COMPASS_LIST_INTERVAL`), only when they differ from the last written. A touch inside
 * the second schedules one read for its end, so the last change is never lost and a walk allocates one string a second.
 *
 * @param ports The reading, writing, clock and timer.
 * @returns The writer.
 */
export function createCompassList(ports: CompassListPorts): CompassList {
  let last = Number.NEGATIVE_INFINITY;
  let written: string | null = null;
  let timer: unknown = null;
  /**
   * Reads the words and writes them if they changed.
   */
  const flush = (): void => {
    timer = null;
    last = ports.now();
    const text = ports.read();
    if (text !== written) {
      written = text;
      ports.write(text);
    }
  };
  return {
    touch: (): void => {
      const wait = last + COMPASS_LIST_INTERVAL - ports.now();
      if (wait <= 0) {
        if (timer !== null) {
          ports.cancel(timer);
        }
        flush();
      } else if (timer === null) {
        timer = ports.later(flush, wait);
      }
    },
    dispose: (): void => {
      if (timer !== null) {
        ports.cancel(timer);
        timer = null;
      }
    },
  };
}
